"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { dataService } from "@/lib/data-service";
import { Lock, Mail, ArrowRight, Loader2, User, Users } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/components/data-provider";
import { useTranslation } from "@/i18n/provider";

function LoginContent() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { refreshData } = useData();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { register, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            email: "",
            password: "",
            fullName: ""
        }
    });

    const redirectParam = searchParams.get("redirect");
    const hasInvite = redirectParam?.includes("invite=");
    const modeParam = searchParams.get("mode");
    const [isSignup, setIsSignup] = useState(!!hasInvite || modeParam === "signup");

    useEffect(() => {
        if (hasInvite || modeParam === "signup") {
            setIsSignup(true);
        }
    }, [hasInvite, modeParam]);

    const onSubmit = async (data: any) => {
        setIsLoading(true);
        try {
            if (isSignup) {
                // SIGNUP LOGIC
                const res = await fetch('/api/auth/signup', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: data.email, // Validation handled by server
                        password: data.password,
                        fullName: data.fullName
                    })
                });
                const result = await res.json();

                if (!res.ok) throw new Error(result.error || t("auth.signupError"));

                // Redirect to Pending Verification
                let redirectUrl = `/pending-verification?email=${encodeURIComponent(data.email)}`;
                if (redirectParam) {
                    redirectUrl += `&redirect=${encodeURIComponent(redirectParam)}`;
                }

                // Use window.location for more reliable redirect
                window.location.href = redirectUrl;

            } else {
                // LOGIN LOGIC
                const res = await fetch('/api/auth/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: data.email,
                        password: data.password
                    })
                });
                const result = await res.json();

                if (!res.ok) throw new Error(result.error || t("auth.loginError"));

                const user = result.user;
                if (user) {
                    localStorage.removeItem('glassy_active_societe');
                    localStorage.removeItem('active_societe_id');
                    localStorage.setItem("glassy_current_user_id", user.id);

                    if (redirectParam) {
                        window.location.href = redirectParam;
                    } else if (user.societes && user.societes.length > 0) {
                        window.location.href = "/";
                    } else {
                        window.location.href = "/onboarding";
                    }
                }
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || t("common.anErrorOccurred"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative z-50 backdrop-blur-xl p-8 rounded-2xl shadow-2xl" style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb' }}>
            {hasInvite && (
                <div className="mb-6 rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)' }}>
                    <div className="p-2 rounded-lg mt-0.5" style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>
                        <Users className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="font-medium text-sm" style={{ color: '#111827' }}>{t("auth.youHaveBeenInvited")}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{t("auth.createAccountToJoinTeam")}</p>
                    </div>
                </div>
            )}

            <div className="text-center mb-8">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
                    <Lock className="h-6 w-6" style={{ color: '#3B82F6' }} />
                </div>
                <h1 className="text-2xl font-bold mb-2" style={{ color: '#111827' }}>
                    {isSignup ? t("auth.signup") : t("auth.login")}
                </h1>
                <p className="text-sm" style={{ color: '#6b7280' }}>
                    {isSignup ? t("auth.joinUsInSeconds") : t("auth.accessYourSpace")}
                </p>
            </div>

            <a
                href={`/api/auth/google/start${redirectParam ? `?redirectUrl=${encodeURIComponent(redirectParam)}` : ''}`}
                className="w-full font-medium py-2.5 rounded-xl flex items-center justify-center gap-3 transition-colors mb-6 shadow-sm hover:opacity-80"
                style={{ background: '#ffffff', color: '#111827', border: '1px solid #e5e7eb' }}
            >
                <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                </svg>
                <span className="text-sm">{t("auth.continueWithGoogle")}</span>
            </a>

            <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full" style={{ borderTop: '1px solid #e5e7eb' }}></div>
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="px-2 uppercase font-medium tracking-wider" style={{ background: 'rgba(255,255,255,0.95)', color: '#6b7280' }}>{t("auth.orClassically")}</span>
                </div>
            </div>

            <form suppressHydrationWarning onSubmit={async (e) => {
                await handleSubmit(onSubmit)(e);
            }} className="space-y-4 relative z-50">
                {isSignup && (
                    <div className="space-y-2 relative z-50">
                        <label className="text-xs font-medium uppercase tracking-wider ml-1" style={{ color: '#6b7280' }}>{t("auth.fullNameLabel")}</label>
                        <div className="relative group z-50">
                            <User className="absolute left-3 top-2.5 h-5 w-5" style={{ color: '#9ca3af' }} />
                            <input
                                {...register("fullName", { required: isSignup })}
                                className="w-full rounded-xl py-2.5 pl-10 pr-4 transition-all relative z-50 outline-none"
                                style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#111827' }}
                                placeholder="Jean Dupont"
                            />
                        </div>
                    </div>
                )}

                <div className="space-y-2 relative z-50">
                    <label className="text-xs font-medium uppercase tracking-wider ml-1" style={{ color: '#6b7280' }}>{t("auth.emailLabel")}</label>
                    <div className="relative group z-50" suppressHydrationWarning>
                        <Mail className="absolute left-3 top-2.5 h-5 w-5" style={{ color: '#9ca3af' }} />
                        <input
                            {...register("email", { required: t("auth.emailRequired"), pattern: { value: /^\S+@\S+$/i, message: t("auth.emailInvalid") } })}
                            type="email"
                            className="w-full rounded-xl py-2.5 pl-10 pr-4 transition-all relative z-50 outline-none"
                            style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#111827' }}
                            placeholder="exemple@email.com"
                            suppressHydrationWarning
                        />
                    </div>
                    {errors.email && <p className="text-xs mt-1 ml-1 font-medium p-1 rounded" style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)' }}>{(errors.email as any).message}</p>}
                </div>

                <div className="space-y-2 relative z-50">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-medium uppercase tracking-wider ml-1" style={{ color: '#6b7280' }}>{t("auth.passwordLabel")}</label>
                        {!isSignup && <a href="/forgot-password" className="text-xs relative z-50 hover:opacity-80" style={{ color: '#3B82F6' }}>{t("auth.forgotPassword")}</a>}
                    </div>
                    <div className="relative group z-50" suppressHydrationWarning>
                        <Lock className="absolute left-3 top-2.5 h-5 w-5" style={{ color: '#9ca3af' }} />
                        <input
                            {...register("password", { required: t("auth.passwordRequired"), minLength: { value: 6, message: t("auth.passwordMin") } })}
                            type="password"
                            className="w-full rounded-xl py-2.5 pl-10 pr-4 transition-all relative z-50 outline-none"
                            style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#111827' }}
                            placeholder={isSignup ? t("auth.passwordMinPlaceholder") : "••••••••"}
                            suppressHydrationWarning
                        />
                    </div>
                    {errors.password && <p className="text-xs mt-1 ml-1 font-medium p-1 rounded" style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)' }}>{(errors.password as any).message}</p>}
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full font-bold py-3 rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] mt-6 relative z-50 cursor-pointer touch-manipulation disabled:opacity-70 disabled:cursor-wait hover:opacity-90"
                    style={{ background: '#3B82F6', color: '#ffffff', boxShadow: '0 10px 15px -3px rgba(59,130,246,0.25)', transform: 'translateZ(0)' }}
                >
                    {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                        <>
                            {isSignup ? t("auth.createMyAccount") : t("auth.signIn")}
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </button>
            </form>

            <div className="mt-8 pt-6 text-center relative z-50" style={{ borderTop: '1px solid #e5e7eb' }}>
                <p className="text-sm" style={{ color: '#6b7280' }}>
                    {isSignup ? t("auth.alreadyHaveAccount") : t("auth.noAccount")} {" "}
                    <button
                        type="button"
                        onClick={() => setIsSignup(!isSignup)}
                        className="hover:underline font-bold relative z-50 cursor-pointer"
                        style={{ color: '#3B82F6' }}
                    >
                        {isSignup ? t("auth.signIn") : t("auth.signup")}
                    </button>
                </p>
            </div>
        </div>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={<div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
            <LoginContent />
        </Suspense>
    );
}
